/**
 * Feature-module kernel — shared between the worker and the frontend.
 *
 * Design: docs/architecture/feature-modules/design.md (v7).
 *
 * This file owns the three cross-side primitives:
 *   - FeatureIdentity        (name + entitlement keys — exists once per feature)
 *   - route contracts        (path + params built once; worker registers
 *                             through them, clients invoke them)
 *   - the matcher engine     (segment-class specificity vector, boundary-safe
 *                             prefix semantics identical to checkPathGate,
 *                             intersection detection, ambiguity rejection)
 *
 * No imports — pure TypeScript, consumable by wrangler and vite alike.
 */

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

export interface FeatureIdentity {
  /** Directory/module name. Default case: equals the single entitlement key. */
  readonly name: string
  /**
   * Canonical @ops/entitlements keys this module carries. Validated against
   * the registry at composition. `null` is the platform discriminant:
   * un-licensed surface, every registration requires a reason.
   */
  readonly entitlements: readonly string[] | null
}

export function defineIdentity(identity: FeatureIdentity): FeatureIdentity {
  if (identity.entitlements !== null && identity.entitlements.length === 0) {
    throw new Error(`feature "${identity.name}": entitlements must be null (platform) or non-empty`)
  }
  return identity
}

// ---------------------------------------------------------------------------
// Matchers
// ---------------------------------------------------------------------------

export type ApiMatcher =
  | { kind: 'prefix'; path: string }
  | { kind: 'pattern'; path: string }

/**
 * Segment/terminal classes for the specificity vector. Higher = more specific.
 *
 * Every vector carries a trailing terminal: EXACT for a matcher that ends at
 * its last segment, REST for one that opens to any suffix (prefix / rest-param).
 * EXACT out-ranks a concrete segment class, which out-ranks REST — so at the
 * boundary an exact contract beats a same-base prefix, positionally, with no
 * length tiebreaker.
 */
const CLASS_EXACT = 3
const CLASS_LITERAL = 2
const CLASS_PARAM = 1
const CLASS_REST = 0

interface NormalizedMatcher {
  /** literal segment text, or null for a :param segment */
  segments: Array<string | null>
  /**
   * true = matches any suffix beyond `segments` — prefix matchers, and
   * patterns whose final segment is a rest-param (`:name*`, consuming one
   * or more remaining segments, e.g. nested docs content slugs).
   */
  rest: boolean
  source: ApiMatcher
}

function stripQuery(path: string): string {
  const q = path.indexOf('?')
  return q === -1 ? path : path.slice(0, q)
}

function toSegments(path: string): string[] {
  return stripQuery(path).split('/').filter((s) => s.length > 0)
}

export function normalizeMatcher(m: ApiMatcher): NormalizedMatcher {
  const raw = toSegments(m.path)
  if (raw.length === 0) throw new Error(`matcher has no segments: ${m.path}`)
  const restParam = raw.length > 0 && /^:[A-Za-z0-9_]+\*$/.test(raw[raw.length - 1])
  if (raw.slice(0, -1).some((seg) => seg.endsWith('*'))) {
    throw new Error(`rest-param must be the final segment: ${m.path}`)
  }
  const body = restParam ? raw.slice(0, -1) : raw
  const segments = body.map((s) => (s.startsWith(':') ? null : s))
  if (m.kind === 'prefix' && (segments.some((s) => s === null) || restParam)) {
    throw new Error(`prefix matcher may not contain :params (use kind: 'pattern'): ${m.path}`)
  }
  return { segments, rest: m.kind === 'prefix' || restParam, source: m }
}

/**
 * Specificity vector: one class per segment, then a mandatory terminal —
 * CLASS_EXACT for a matcher that ends at its last segment, CLASS_REST for one
 * that opens to any suffix (prefix matcher / rest-param pattern). Compared
 * lexicographically, position-by-position. The terminal makes the exact-vs-
 * prefix boundary decide on class (EXACT > REST) rather than on vector length,
 * so an exact contract out-ranks a same-base prefix directly.
 */
export function specificityVector(n: NormalizedMatcher): number[] {
  const v: number[] = n.segments.map((s) => (s === null ? CLASS_PARAM : CLASS_LITERAL))
  v.push(n.rest ? CLASS_REST : CLASS_EXACT)
  return v
}

export function compareSpecificity(a: number[], b: number[]): number {
  const len = Math.min(a.length, b.length)
  for (let i = 0; i < len; i += 1) {
    if (a[i] !== b[i]) return b[i] - a[i] // higher class first
  }
  // Every vector ends in a terminal (EXACT or REST) and no segment class equals
  // a terminal, so no vector is ever a proper prefix of another: reaching here
  // means the vectors are identical — genuinely equal specificity. Ambiguity
  // (equal + intersecting) is caught in rankMatchers; there is no length
  // tiebreaker, by construction.
  return 0
}

/**
 * Do two matchers admit at least one common path?
 * Segment-by-segment: literals must be equal; a param is compatible with
 * anything at its position; a rest-wildcard is compatible with any suffix.
 * A non-rest matcher only matches exact-length paths, so differing lengths
 * intersect only through the shorter side's rest.
 */
export function matchersIntersect(a: NormalizedMatcher, b: NormalizedMatcher): boolean {
  const len = Math.min(a.segments.length, b.segments.length)
  for (let i = 0; i < len; i += 1) {
    const sa = a.segments[i]
    const sb = b.segments[i]
    if (sa !== null && sb !== null && sa !== sb) return false
  }
  if (a.segments.length === b.segments.length) return true
  const [longer, shorter] = a.segments.length > b.segments.length ? [a, b] : [b, a]
  void longer
  return shorter.rest
}

/** Does `path` match a normalized matcher? Boundary-safe, query-safe. */
export function matcherMatches(n: NormalizedMatcher, path: string): boolean {
  const segs = toSegments(path)
  if (n.rest) {
    // rest-param patterns (`:name*`) consume ONE OR MORE segments; bare
    // prefixes also match their exact path.
    const isRestParam = n.source.kind === 'pattern'
    if (segs.length < n.segments.length + (isRestParam ? 1 : 0)) return false
  } else if (segs.length !== n.segments.length) {
    return false
  }
  for (let i = 0; i < n.segments.length; i += 1) {
    const want = n.segments[i]
    if (want !== null && segs[i] !== want) return false
  }
  return true
}

export interface RankedMatcher<T> {
  normalized: NormalizedMatcher
  vector: number[]
  /** HTTP methods this entry serves; null = method-agnostic. */
  methods: readonly string[] | null
  payload: T
}

function methodsOverlap(a: readonly string[] | null, b: readonly string[] | null): boolean {
  if (a === null || b === null) return true
  return a.some((m) => b.includes(m))
}

/**
 * Build a ranked matcher table. Throws on ambiguity: two matchers that
 * intersect with equal specificity vectors (regardless of owner) — the
 * composition error the design mandates (5.3).
 */
export function rankMatchers<T>(
  entries: Array<{ matcher: ApiMatcher; methods?: readonly string[] | null; payload: T }>,
): Array<RankedMatcher<T>> {
  const ranked = entries.map(({ matcher, methods, payload }) => {
    const normalized = normalizeMatcher(matcher)
    return { normalized, vector: specificityVector(normalized), methods: methods ?? null, payload }
  })
  for (let i = 0; i < ranked.length; i += 1) {
    for (let j = i + 1; j < ranked.length; j += 1) {
      const a = ranked[i]
      const b = ranked[j]
      if (
        compareSpecificity(a.vector, b.vector) === 0 &&
        matchersIntersect(a.normalized, b.normalized) &&
        methodsOverlap(a.methods, b.methods)
      ) {
        throw new Error(
          `ambiguous matchers (equal specificity, intersecting): ` +
            `"${a.normalized.source.path}" vs "${b.normalized.source.path}"`,
        )
      }
    }
  }
  ranked.sort((a, b) => compareSpecificity(a.vector, b.vector))
  return ranked
}

/**
 * First (most specific) matching entry, or null. `method` filters
 * method-scoped entries; omit it for method-agnostic lookups (the
 * entitlement gate — same-path method pairs share their key).
 */
export function matchRanked<T>(
  table: Array<RankedMatcher<T>>,
  path: string,
  method?: string,
): RankedMatcher<T> | null {
  for (const entry of table) {
    if (method !== undefined && entry.methods !== null && !entry.methods.includes(method)) continue
    if (matcherMatches(entry.normalized, path)) return entry
  }
  return null
}

// ---------------------------------------------------------------------------
// Route contracts (mandatory for every frontend-consumed endpoint — 5.3.1)
// ---------------------------------------------------------------------------

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export interface EndpointContract {
  readonly method: HttpMethod
  readonly path: string
  /** Attributed entitlement key; must be one of the identity's keys. */
  readonly entitlement: string | null
  /** Build a concrete URL path from :params (and optional query). */
  build(params?: Record<string, string | number>, query?: Record<string, string | number | boolean | undefined>): string
  /** The matcher the worker registers with. */
  matcher(): ApiMatcher
}

export function endpoint(
  method: HttpMethod,
  path: string,
  opts: { entitlement: string | null },
): Omit<EndpointContract, never> {
  const hasParams = toSegments(path).some((s) => s.startsWith(':'))
  return {
    method,
    path,
    entitlement: opts.entitlement,
    build(params = {}, query) {
      const built = toSegments(path)
        .map((seg) => {
          if (!seg.startsWith(':')) return seg
          const rest = seg.endsWith('*')
          const key = rest ? seg.slice(1, -1) : seg.slice(1)
          const value = params[key]
          if (value === undefined) throw new Error(`contract ${method} ${path}: missing param :${key}`)
          if (rest) {
            // rest-param: a multi-segment path value; encode each segment,
            // keep the separators (nested docs slugs etc.).
            return String(value).split('/').filter(Boolean).map(encodeURIComponent).join('/')
          }
          return encodeURIComponent(String(value))
        })
        .join('/')
      let url = `/${built}`
      if (query) {
        const qs = Object.entries(query)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join('&')
        if (qs) url += `?${qs}`
      }
      return url
    },
    matcher() {
      return hasParams ? { kind: 'pattern', path } : { kind: 'pattern', path }
    },
  }
}

/**
 * Define a feature's contracts. Validates every endpoint's entitlement
 * attribution against the identity: single-key modules may omit nothing —
 * the key must match; multi-key modules must attribute one of their keys;
 * platform (entitlements: null) endpoints must carry entitlement: null.
 */
export function defineContracts<T extends Record<string, EndpointContract>>(
  identity: FeatureIdentity,
  contracts: T,
): T {
  for (const [cname, c] of Object.entries(contracts)) {
    if (identity.entitlements === null) {
      if (c.entitlement !== null) {
        throw new Error(`platform contract ${cname} must have entitlement: null`)
      }
    } else if (c.entitlement === null || !identity.entitlements.includes(c.entitlement)) {
      throw new Error(
        `contract ${cname}: entitlement "${String(c.entitlement)}" is not carried by module "${identity.name}"`,
      )
    }
  }
  return contracts
}
